import { memo, Suspense, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { PendingActions } from "@mui/icons-material";
import { Box, Checkbox, Divider } from "@mui/material";
import { isBoolean, isEmpty, isNil, isObject, size } from "lodash";
import CreateUpdateLoanConditionForm from "../../../../components/forms/CreateUpdateLoanConditionForm";
import Flx from "../../../../components/layout/Flx";
import LoadingFallback from "../../../../components/LoadingFallback";
import TabContent from "../../../../components/navigation/TabContent";
import ToggleTabSwitcher from "../../../../components/navigation/ToggleTabSwitcher";

import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { htmlStringToText } from "../../../../_src_shared/utils/functions/htmlStringToText";
import LoanConditionCommentInput from "../../../../components/comments/LoanConditionCommentInput";
import FileUploadProcess from "../../../../components/processes/FileUploadProcess";
import CommentsRenderer from "../../../../components/renderers/CommentsRenderer";
import DocumentIdsRenderer from "../../../../components/renderers/DocumentIdsRenderer";
import BasicFilesTable from "../../../../components/tables/BasicFilesTable";
import Txt from "../../../../components/typography/Txt";
import InfoRowItem, {
  InfoStackedItem,
} from "../../../../components/ui/data/InfoRowItem";
import { useChecklistRouteHook } from "../../../../hooks/useChecklistRouteHook";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import { loanFetchedDataUwRequirementsSet } from "../../../../redux/reducers/loanFetchedDataReducer";
import { getUserFullNameFromEmail } from "../../../../utils/functions/getUserFullNameFromEmail";
import DrilldownSidebarPane from "../DrilldownSidebarPane";
import { amber, green, grey, orange, red } from "@mui/material/colors";
import { formatDateShort } from "../../../../utils/dates/formatDateShort";
import { formatDateTime } from "../../../../_src_shared/utils/functions/formatDateTime";
import JsonPreview from "../../../../components/common/JsonPreview";
import HtmlEditor from "../../../../components/html/HtmlEditor";

const OBJECT_TYPE_ITEM_LABEL = "Loan Condition";

const DrilldownSidebarAddEditLoanCondition = memo(() => {
  const condition = useSelector((state) => state.sidebar?.state);

  const isUpdating = useMemo(
    () => isObject(condition) && !isEmpty(condition),
    [condition],
  );

  return (
    <Suspense fallback={<LoadingFallback />}>
      <DrilldownSidebarPane
        title={
          isUpdating
            ? `Manage ${OBJECT_TYPE_ITEM_LABEL}`
            : `Add ${OBJECT_TYPE_ITEM_LABEL}`
        }
        icon={<PendingActions className="thin" />}
        initialWidth={"600px"}
        bodyPadding={isUpdating ? 0 : 2.8}
        // bodySx={{ pt: 2 }}
      >
        <LoanConditionSidebarContentRenderer
          isUpdating={isUpdating}
          condition={condition}
        />
      </DrilldownSidebarPane>
    </Suspense>
  );
});

// const ExpandableTabContent = ({ title, children }) => {
//   const [show, setShow] = useState(true);
//   return (
//     <Accordion
//       onChange={() => setShow(!show)}
//       expanded={show}
//       sx={{
//         m: "0 !important",
//         borderRadius: "4px",
//         boxShadow: "0 0 0px 1px #dde0e4",
//         // overflow: "hidden",
//       }}
//     >
//       <AccordionSummary
//         sx={{
//           borderBottom: "1px solid #dde0e4",
//           background: "#ffffff",
//         }}
//         expandIcon={<ExpandMoreRounded sx={{ color: "#232a31" }} />}
//       >
//         <Flx fw jb ac sx={{ color: "#232a31" }}>
//           <Txt bold variant="subtitle2" sx={{ color: "inherit" }}>
//             {title}
//           </Txt>
//         </Flx>
//       </AccordionSummary>
//       <AccordionDetails sx={{ background: "#fafaf8" }}>
//         {children}
//       </AccordionDetails>
//     </Accordion>
//   );
// };

const LoanConditionSidebarContentRenderer = ({ isUpdating, condition }) => {
  if (isUpdating) {
    return <ManageConditionSidebarContent condition={condition} />;
  }
  return <CreateUpdateLoanConditionForm />;
};
const TAB_TYPES = {
  DETAILS: "Condition Details",
  EDIT: "Edit Condition",
  FILES: "Related Files",
  JSON: "JSON",
};

const ManageConditionSidebarContent = ({ condition }) => {
  const [tab, setTab] = useState(0);

  return (
    <Flx
      column
      sx={{
        flexGrow: 1,
        height: "100%",
        overflow: "hidden",
        // borderTop: "1px solid rgb(224, 221, 213)",
        // background: "#fafaf8",
      }}
    >
      <Box
        sx={{
          borderBottom: "1px solid rgb(224, 221, 213)",
          pl: 1.5,
          background: "#ffffff",
        }}
      >
        <ToggleTabSwitcher
          tabs={[
            TAB_TYPES.DETAILS,
            TAB_TYPES.EDIT,
            `${TAB_TYPES.FILES} (${size(condition?.uploadedDocumentIds) || 0})`,
            TAB_TYPES.JSON,
          ]}
          value={tab}
          variant={"underline"}
          onChange={setTab}
          useIndex
        />
      </Box>

      <TabContent activeTab={tab} tabKey={0}>
        <ManageConditionDetailsTab data={condition} />
      </TabContent>
      <TabContent activeTab={tab} tabKey={1}>
        <Box sx={{ p: 3, height: "100%", overflowY: "auto" }}>
          <CreateUpdateLoanConditionForm isUpdating initialValues={condition} />
        </Box>
      </TabContent>
      <TabContent activeTab={tab} tabKey={2}>
        <ManageConditionFilesTab data={condition} />
      </TabContent>
      <TabContent activeTab={tab} tabKey={3}>
        <Box sx={{ p: 2 }}>
          <JsonPreview values={condition} defaultCollapse={3} />
        </Box>
      </TabContent>

      {/* <ExpandableTabContent title={"Details"} activeTab={tab} tabKey={0}>
        <ManageConditionDetailsTab data={condition} />
      </ExpandableTabContent>
      <ExpandableTabContent title={"Edit"} activeTab={tab} tabKey={1}>
        <Box sx={{ p: 3, height: "100%", overflowY: "auto" }}>
          <CreateUpdateLoanConditionForm isUpdating initialValues={condition} />
        </Box>
      </ExpandableTabContent>
      <ExpandableTabContent title={"Files"} activeTab={tab} tabKey={2}>
        <ManageConditionFilesTab data={condition} />
      </ExpandableTabContent> */}
    </Flx>
  );
};

const ManageConditionDetailsTab = ({ data }) => {
  const uwRequirements = useSelector(
    (state) => state.loanFetchedData?.uwRequirements,
  );
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanDocuments = loanDrilldown?.loanDocuments;
  const associatedLoanId = loanDrilldown?._id;

  const conditionId = data?._id;
  const conditionData = uwRequirements?.find(
    (cond) => cond._id === conditionId,
  );

  const users = useSelector((state) => state?.users);

  const statusStyle = useMemo(() => {
    switch (conditionData?.status) {
      case "Second Level Review":
      case "In Progress":
        return { color: amber[900] };
      case "Rejected":
        return { color: red[800] };
      case "Accepted":
        return { color: green[800] };
      default:
        return {};
    }
  }, [conditionData?.status]);

  return (
    <>
      <Flx
        sx={{ overflow: "auto", flexGrow: 1, height: "100%", p: 2 }}
        column
        g={0.5}
      >
        <SectionBox>
          <Flx column g={2.5}>
            <FlxGrid width="150px" grow={1} gap={3}>
              <InfoStackedItem
                fontSize="11px"
                label={"Condition Status"}
                value={conditionData?.status}
                valueSx={statusStyle}
              />
              {conditionData?.updatedBy && (
                <InfoStackedItem
                  fontSize="11px"
                  label={"Last Updated By"}
                  value={`${formatDateShort(conditionData?.updateDateTime)} by ${getUserFullNameFromEmail(users, conditionData?.updatedBy)}`}
                />
              )}
              {conditionData?.commentDateTime && (
                <InfoStackedItem
                  fontSize="11px"
                  label={"Created"}
                  value={
                    conditionData?.createdBy
                      ? `${formatDateShort(conditionData?.commentDateTime)} by ${getUserFullNameFromEmail(users, conditionData?.createdBy)}`
                      : formatDateShort(conditionData?.commentDateTime)
                  }
                />
              )}
              {conditionData?.autoGenerated &&
                isNil(conditionData?.createdBy) && (
                  <InfoStackedItem
                    fontSize="11px"
                    label={"Auto Generated"}
                    value={
                      isBoolean(conditionData?.autoGenerated) &&
                      conditionData?.autoGenerated
                        ? "Yes"
                        : "No"
                    }
                  />
                )}
              {/* <InfoStackedItem
                fontSize="11px"
                label={"Created Date"}
                value={formatDate(conditionData?.commentDateTime)}
              /> */}

              {conditionData?.dueDate && (
                <InfoStackedItem
                  hideIfEmpty
                  fontSize="11px"
                  label={"Due Date"}
                  value={formatDate(conditionData?.dueDate)}
                />
              )}
            </FlxGrid>

            {conditionData?.resolutionNotes || conditionData?.resolvedBy ? (
              <Box
                sx={{
                  background: grey[100],
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${grey[300]}`,
                }}
              >
                <Flx column gap={3} grow={1}>
                  <InfoStackedItem
                    fontSize="11px"
                    label={"Resolved By"}
                    value={getUserFullNameFromEmail(
                      users,
                      conditionData?.resolvedBy,
                    )}
                  />
                  <InfoStackedItem
                    ellipsis={false}
                    fontSize="11px"
                    label={"Resolution Notes"} //Condition Details
                    value={htmlStringToText(conditionData?.resolutionNotes)}
                  />
                </Flx>
              </Box>
            ) : null}
          </Flx>
        </SectionBox>
        <SectionBox sx={{ gap: 1 }}>
          <Flx column g={4}>
            {conditionData?.docType ? (
              <>
                <Flx fw jb ac gap={2}>
                  <InfoStackedItem
                    fontSize="11px"
                    label={"What's Needed"} //Condition Details
                    value={<>Missing Document: {data?.docType}</>}
                  />
                  <InfoStackedItem
                    fontSize="11px"
                    label={"Document Status"}
                    value={conditionData?.docStipStatus}
                  />
                </Flx>
                <InfoStackedItem
                  fontSize="11px"
                  label={"Details"} //Condition Details
                  value={htmlStringToText(data?.htmlString)}
                />
              </>
            ) : (
              <>
                <InfoStackedItem
                  fontSize="11px"
                  label={"What's Needed"} //Condition Details
                  value={
                    <HtmlEditor
                      // minHeight={60}
                      value={conditionData?.htmlString} // Pass initial value only
                      suppressOuterBorder
                      readOnly
                    />
                  }
                  // value={htmlStringToText(conditionData?.htmlString)}
                />
              </>
            )}

            {!isEmpty(conditionData?.uploadedDocumentIds) ? (
              <InfoStackedItem
                ellipsis={false}
                fontSize="11px"
                label={"Uploaded Files"} //Condition Details
                value={
                  isEmpty(conditionData?.uploadedDocumentIds) ? (
                    <Txt sx={{ fontStyle: "italic" }} secondary>
                      No files uploaded yet
                    </Txt>
                  ) : (
                    <DocumentIdsRenderer
                      documents={loanDocuments}
                      documentIds={conditionData?.uploadedDocumentIds}
                    />
                  )
                }
              />
            ) : null}
          </Flx>
          <Divider sx={{ my: 2 }} />
          <Flx column g={2}>
            <Box sx={{ px: 0.5 }}>
              <CommentsRenderer
                onlyComments
                comments={
                  conditionData?.comments || conditionData?.conditionComments
                }
              />
            </Box>
            <LoanConditionCommentInput
              loanId={associatedLoanId}
              conditionId={conditionData?._id}
              inputTypeBasic
            />
          </Flx>
        </SectionBox>
        {/* <SectionBox>
          <Flx column g={2}>
            <CommentsRenderer
              onlyComments
              comments={
                conditionData?.comments || conditionData?.conditionComments
              }
            />
            <LoanConditionCommentInput
              loanId={associatedLoanId}
              conditionId={conditionData?._id}
              inputTypeBasic
            />
          </Flx>
        </SectionBox> */}
        <Divider sx={{ my: 3 }}>
          <Txt secondary>Additional Condition Details</Txt>
        </Divider>
        <SectionBox>
          <FlxGrid width="150px" grow={1} gap={3}>
            <InfoStackedItem
              fontSize="11px"
              label={"Last Updated"}
              value={formatDate(conditionData?.updateDateTime)}
            />
            <InfoStackedItem
              fontSize="11px"
              label={"Last Updated By"}
              value={getUserFullNameFromEmail(users, conditionData?.updatedBy)}
            />

            <InfoStackedItem
              fontSize="11px"
              label={"Requirement Type"}
              value={conditionData?.requirementType}
            />
            <InfoStackedItem
              fontSize="11px"
              label={"Created Date/Time"}
              value={formatDateTime(conditionData?.commentDateTime)}
            />
            <InfoStackedItem
              fontSize="11px"
              label={"Created By"}
              value={getUserFullNameFromEmail(users, conditionData?.createdBy)}
            />
            <InfoStackedItem
              fontSize="11px"
              label={"Display Externally"}
              value={
                isBoolean(conditionData?.displayExternally) &&
                conditionData?.displayExternally
                  ? "Yes"
                  : "No"
              }
            />
            <InfoStackedItem
              fontSize="11px"
              label={"Auto Generated"}
              value={
                isBoolean(conditionData?.autoGenerated) &&
                conditionData?.autoGenerated
                  ? "Yes"
                  : "No"
              }
            />
          </FlxGrid>
        </SectionBox>
      </Flx>
    </>
  );
};

const ManageConditionFilesTab = ({ data }) => {
  const uwRequirements = useSelector(
    (state) => state.loanFetchedData?.uwRequirements,
  );
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanDocuments = loanDrilldown?.loanDocuments;
  const associatedLoanId = loanDrilldown?._id;

  const conditionId = data?._id;
  const conditionData = uwRequirements?.find(
    (cond) => cond._id === conditionId,
  );
  const files = useMemo(() => {
    return loanDocuments?.filter((doc) =>
      data?.uploadedDocumentIds?.includes(doc._id),
    );
  }, [loanDocuments, data]);
  return (
    <>
      <Flx
        sx={{ overflow: "auto", flexGrow: 1, height: "100%", p: 2 }}
        column
        g={0.5}
      >
        <UploadFileToLoanConditionProcess
          loanId={loanDrilldown?._id}
          conditionId={data?._id}
          docGroup={data?.docGroup}
          docType={data?.docType}
        />
        <SectionBox sx={{ gap: 1, p: 0 }}>
          <BasicFilesTable files={files} showStatus hideIfEmpty />
        </SectionBox>
      </Flx>
    </>
    // </Flx>
  );
};

const UploadFileToLoanConditionProcess = ({
  loanId,
  conditionId,
  docType,
  docGroup,

  keyName,
}) => {
  const { loading, uploadFilesToUnderwritingRequirement } =
    useChecklistRouteHook();
  const { getLoanById } = useUnderwritingHook();
  const dispatch = useDispatch();

  const handleOnSubmit = (f, { onSuccessFn } = {}) => {
    uploadFilesToUnderwritingRequirement({
      dealId: loanId,
      requirementId: conditionId,
      files: f,
      onSuccessFn: (d) => {
        dispatch(loanFetchedDataUwRequirementsSet(d));
        getLoanById({
          loanId,
          onSuccessFn: (data) => {
            dispatch(loanDrilldownSet(data));
            onSuccessFn?.();
          },

          // onFailFn: () => dispatch(loanDrilldownSet({})),
        });
      },
    });
  };
  return (
    <Flx column g={0.5}>
      {/* <Txt>Relevant Documents</Txt> */}
      <FileUploadProcess
        loanId={loanId}
        docType={docType}
        borderSize={2}
        disableCategoryAndTypeSelection
        disableCategoryAndTypeSelectionCount
        docGroup={docGroup}
        categoryAndTypeLabel={`Document Category`}
        keyName={keyName}
        onSubmit={handleOnSubmit}
        loading={loading}
      />
      <Divider sx={{ my: 3 }} />
    </Flx>
  );
};

const SectionBox = ({ children, sx }) => {
  return (
    <Box
      sx={{
        border: `1px solid ${grey[300]}`,
        // border: "1px solid rgb(224, 221, 213)",
        // p: 1,
        p: 1.5,
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        // gap: 4,
        mb: 2,
        background: grey[50],
        // background: "#ffffff",
        // background: "#fafaf8",
        // background: "#fafbfd",
        // background: grey[50],
        // overflow: "hidden",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};
export default DrilldownSidebarAddEditLoanCondition;
